<script setup lang="ts">
import { fr } from "@nuxt/ui/locale";
import type { GlobalDataResource } from "@carnet/api/backoffice";
import avertaRegularUrl from "@carnet/ui/fonts/averta_regular.woff2?url";
import avertaSemiboldUrl from "@carnet/ui/fonts/averta_semibold.woff2?url";

const { online } = useNetworkStatus();
const { t } = useI18n();
const appStoreId = useRuntimeConfig().public.APPSTORE_APP_ID;

// Membre courant exposé aux composants partagés (@carnet/ui) via inject (cf. useCurrentMemberId).
// `provide` DOIT rester synchrone (avant tout await) : appelé après un `await` de haut niveau,
// le contexte d'instance Vue est perdu (asyncContext non activé) et le provide échoue en
// silence → inject retombe sur null et l'UI de validation réapparaît à tort pour l'encadrant.
// On expose donc la ref tout de suite, puis on attend la résolution (SSR-correct, pas de
// mismatch sur les badges de validation). Sur page publique, useProfile renvoie null sans fetch.
const { isBlocking, message } = useAppStatus();
const profileAsync = isBlocking.value ? null : useProfileData({ watchSession: true });
provide(currentMemberIdKey, computed(() => profileAsync?.data.value?.id ?? null));
// GlobalData (settings d'upload, etc.) exposé aux composants partagés via provide (cf.
// useAppGlobalData) : ref synchrone du cache "global-data", chauffé par les pages qui en ont besoin.
const { data: globalData } = useNuxtData<GlobalDataResource | null>("global-data");
provide(globalDataKey, computed(() => globalData.value ?? null));

const heroArticlesAsync = useHeroArticles();
const defaultSeoImage = computed(() => {
  const hero = heroArticlesAsync.data.value.landscape ?? heroArticlesAsync.data.value.portrait;
  const defaultAlt = t("seo.default.imageAlt");
  return seoImageFrom(hero?.mainImage, hero?.title ?? defaultAlt) ?? { ...FALLBACK_SEO_IMAGE, alt: defaultAlt };
});
useSeoImage(defaultSeoImage);
useSeoCanonical();

if (profileAsync) await profileAsync;
await heroArticlesAsync;

const badgeCount = useBadgeCount();

if (import.meta.client) {
  const router = useRouter();
  router.beforeEach((to, from) => {
    console.log("[NAV beforeEach]", from.fullPath, "→", to.fullPath);
  });
  router.afterEach((to, from, failure) => {
    console.log("[NAV afterEach]", from.fullPath, "→", to.fullPath, failure ? `FAILURE: ${failure.type}` : "OK");
  });
}

useHead({
  htmlAttrs: {
    lang: "fr",
    class: "has-[body[data-app-ready=true]]:scroll-smooth scroll-pt-(--ui-header-height,72px)",
  },
  titleTemplate: (chunk) => formatDocumentTitle(chunk ?? undefined, t("app.title"), badgeCount.value),
  meta: appStoreId ? [{ name: "apple-itunes-app", content: `app-id=${appStoreId}` }] : [],
  link: import.meta.dev
    ? []
    : [
        { rel: "preload", as: "font", type: "font/woff2", href: avertaRegularUrl, crossorigin: "anonymous" },
        { rel: "preload", as: "font", type: "font/woff2", href: avertaSemiboldUrl, crossorigin: "anonymous" },
      ],
});

useSeoMeta({
  description: () => t("seo.default.description"),
  ogType: "website",
  ogSiteName: () => t("app.title"),
  ogLocale: "fr_FR",
});

// Sur petit écran, les toasts passent en haut (un toast en bas masque les CTA / la nav).
const isMobile = useIsMobile();
const toaster = computed(() => ({ position: isMobile.value ? "top-center" : "bottom-right" }) as const);
</script>

<template>
  <UApp :locale="fr" :toaster>
    <AppStatusBlockingScreen v-if="isBlocking" :message="message" />
    <template v-else>
      <AppOfflineBanner :visible="!online" />
      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>
      <WebCookieConsentBanner />
      <WebCookieConsentModal />
      <AppStatusDialog />
    </template>
  </UApp>
</template>
